import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Stack,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
    Box,
    Fade,
    TextField,
    FormControlLabel,
    Checkbox,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import FilterPanelFrame from "../../components/fitlerPanel/filterPanelFrame"
import FilterTabBarFrame from "../../components/fitlerPanel/filterTabBarFrame"
import SelectedFilterTab from "../../components/fitlerPanel/selectedFilterTab"
import AppControlledSelectKwarg from "../../components/inputs/controlled/appControlledSelectKwarg"
import AppPaper from "../../components/surfaces/appPaper"
import GenSearchResults, {
    IDatabaseSearchResult,
} from "../../components/tables/ttr/genSearchResults"
import DisplayIf from "../../components/wrappers/displayIf"
import { Datasets, LCTOnlyDatasets } from "../../constants/enums"
import {
    DEMAND_KW,
    DEVICE_MODEL_FILTER_KW,
    FILTER_PANEL_SLIDE_MS,
    GENERATION_KW,
    SYSTEM_FILTER_REF_KW,
} from "../../constants/generalConstants"
import { useGenSearchURLFilterParams } from "../../hooks/useGenDeriveFilterValuesFromUrl"
import { useGetUserData } from "../../hooks/useGetUserData"
import { DEMAND_DEVICE_SEARCH_NAV_LINK } from "../../router/routes"
// eslint-disable-next-line max-len
import useMultiFetcherGenFilterReferenceData from "../../services/middleware/hooks/useMultiFetcherGenFilterReference"
import useMultiFetcherSearchGenDevices, {
    GEN_PAGE_SIZE,
} from "../../services/middleware/hooks/useMultiFetcherSearchGenDevices"
import {
    CATEGORY_REF_KEY,
    COMPLIANCE_STATUS_REF_KEY,
    DEVICE_TYPE_REF_KEY,
    JURISDICTION_REF_KEY,
    MANUFACTURER_REF_KEY,
    PHASE_REF_KEY,
} from "../../services/middleware/mappers/genFilterReferenceMapper"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

export const MAX_CAPACITY = 100

export type TFilterSelect = Record<string, string | number> | null

/**
 * Webpage for low carbon technology database search interface for generation
 * devices
 * @example
 * <GenDeviceSearch />
 * */
interface GenDeviceSearchProps {
    onDeviceSelect?: (device: IDatabaseSearchResult) => void
    searchConfig?: {
        deviceType?: string
        dataset?: string
        defaultFilters?: Record<string, any>
        availableDatasets?: string[]
        isPopupMode?: boolean // Add flag to detect popup mode
    }
    // Add props for external filter visibility control
    showFilters?: boolean
    setShowFilters?: (show: boolean) => void
}

// Define defaultProps for the component
const defaultProps = {
    onDeviceSelect: () => {
        /* intentionally empty */
    },
    searchConfig: {
        deviceType: "",
        dataset: "",
        isPopupMode: false, // Default to regular mode
    },
    showFilters: undefined,
    setShowFilters: undefined,
}

const GenDeviceSearch = ({
    onDeviceSelect,
    searchConfig = defaultProps.searchConfig,
    showFilters: externalShowFilters,
    setShowFilters: externalSetShowFilters,
}: GenDeviceSearchProps): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const {
        isEnaSysAdmin,
        isManuApproved,
        isDnoApproved,
        isDemandAssessor,
        isGenAssessor,
    } = useGetUserData()
    const {
        data,
        isError,
        isLoading,
        isValidating,
        page,
        setPage,
        isFinalPage,
        mutate,
    } = useMultiFetcherSearchGenDevices(searchParams)
    const { referenceData, isLoading: refDataLoading } =
        useMultiFetcherGenFilterReferenceData()

    const {
        complianceStatus,
        phases,
        jurisdiction,
        manufacturer,
        category,
        deviceType,
        capacityMin,
        capacityMax,
        systemRef,
        deviceModel,
        orderBy,
        orderDirection,
        setFilterValue,
        setOrderBy,
        setFilterFromSlider,
        clearAllFilters,
        setFilterFromTextfield,
        isSubscribed,
        isThreadUnread,
    } = useGenSearchURLFilterParams(
        searchParams,
        setSearchParams,
        referenceData,
        refDataLoading
    )

    // Refetch search results on page focus
    useEffect(() => {
        mutate()
    }, [mutate])

    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localSliderValue, setLocalSliderValue] = useState<[number, number]>([
        capacityMin,
        capacityMax,
    ])
    const [localSystemRef, setLocalSystemRef] = useState<string>(systemRef)
    const [localDeviceModel, setLocalDeviceModel] =
        useState<string>(deviceModel)

    /**
     * Called when slider is moved is committed. Updates slider local state
     * but not URL params.
     * @param e_ - MUI slider event, not used
     * @param newValue - array of upper and lower slider value
     */
    const onSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalSliderValue(newValue as [number, number])
    }

    /**
     * Clears all local state and URL search params
     */
    const clearAllLocalAndUrl = (): void => {
        setLocalSystemRef("")
        setLocalDeviceModel("")
        setLocalSliderValue([0, MAX_CAPACITY])
        clearAllFilters()
    }

    // Check if we're in popup mode
    const isPopupMode = searchConfig?.isPopupMode === true

    // Use internal or external state for filter visibility
    const [internalShowFilters, setInternalShowFilters] =
        useState<boolean>(!isPopupMode)

    // Use either the external state if provided, or the internal state
    const showFiltersState =
        externalShowFilters !== undefined
            ? externalShowFilters
            : internalShowFilters
    const setShowFiltersState = externalSetShowFilters || setInternalShowFilters

    // Add effect to apply initial filters when component mounts
    useEffect(() => {
        if (isPopupMode) {
            // For popup mode, automatically filter for compliant devices
            const compliantStatusObject = (
                referenceData?.[COMPLIANCE_STATUS_REF_KEY] || []
            ).find((status: any) => status.StatusShort === "Compliant")

            // Only set the filter if we found a matching status
            if (compliantStatusObject) {
                setFilterValue(compliantStatusObject, COMPLIANCE_STATUS_REF_KEY)
            }
        }

        // Set any other initial filters from searchConfig if provided
        if (searchConfig?.deviceType && !isPopupMode) {
            // Don't set device type filter in popup mode, just like for demand devices
            setFilterFromTextfield(
                searchConfig.deviceType,
                DEVICE_MODEL_FILTER_KW
            )
        }
    }, [
        isPopupMode,
        referenceData,
        setFilterValue,
        searchConfig,
        setFilterFromTextfield,
    ])

    // Handle device selection from search results
    const handleDeviceSelect = (device: IDatabaseSearchResult): void => {
        if (onDeviceSelect) {
            onDeviceSelect(device)
        }
    }

    const isManuTitle = isManuApproved && !isEnaSysAdmin
    return (
        <Grid container spacing={GRID_SPACING}>
            <FilterTabBarFrame
                isValidating={isValidating}
                dataset={Datasets.TTR}
                titleToDisplayInBaseColour={isManuTitle ? "My" : "Search the"}
                titleToDisplayInPrimaryColour={
                    isManuTitle
                        ? "Generation Devices"
                        : "ENA Type Test Register"
                }
            >
                <SelectedFilterTab
                    name="Manufacturer"
                    value={manufacturer?.Manufacturer}
                    onDelete={() => setFilterValue(null, MANUFACTURER_REF_KEY)}
                />

                <SelectedFilterTab
                    name="Category"
                    value={category?.DeviceCategory}
                    onDelete={() => setFilterValue(null, CATEGORY_REF_KEY)}
                />
                <SelectedFilterTab
                    name="Type"
                    value={deviceType?.DeviceType}
                    onDelete={() => setFilterValue(null, DEVICE_TYPE_REF_KEY)}
                />
                <SelectedFilterTab
                    name="Min Capacity"
                    value={localSliderValue[0]}
                    show={capacityMin !== 0}
                    onDelete={() => {
                        const newValues: [number, number] = [0, capacityMax]
                        setLocalSliderValue(newValues)
                        setFilterFromSlider(new Event("void"), newValues)
                    }}
                />
                <SelectedFilterTab
                    name="Max Capacity"
                    value={localSliderValue[1]}
                    show={capacityMax !== MAX_CAPACITY}
                    onDelete={() => {
                        const newValues: [number, number] = [
                            capacityMin,
                            MAX_CAPACITY,
                        ]
                        setLocalSliderValue(newValues)
                        setFilterFromSlider(new Event("void"), newValues)
                    }}
                />

                <SelectedFilterTab
                    name="Status"
                    value={complianceStatus?.StatusShort}
                    onDelete={() =>
                        setFilterValue(null, COMPLIANCE_STATUS_REF_KEY)
                    }
                />
                <SelectedFilterTab
                    name="Phase"
                    value={phases?.Phase}
                    onDelete={() => setFilterValue(null, PHASE_REF_KEY)}
                />
                <SelectedFilterTab
                    name="System Ref"
                    value={systemRef}
                    onDelete={() => {
                        setLocalSystemRef("")
                        setFilterFromTextfield("", SYSTEM_FILTER_REF_KW)
                    }}
                />
                <SelectedFilterTab
                    name="Model"
                    value={deviceModel}
                    onDelete={() => {
                        setLocalDeviceModel("")
                        setFilterFromTextfield("", DEVICE_MODEL_FILTER_KW)
                    }}
                />
                <SelectedFilterTab
                    name="Jurisdiction"
                    value={jurisdiction?.[JURISDICTION_REF_KEY]}
                    onDelete={() => {
                        setFilterValue(null, JURISDICTION_REF_KEY)
                    }}
                />

                <Fade
                    in={
                        !!manufacturer ||
                        !!deviceType ||
                        !!category ||
                        !!complianceStatus ||
                        !!phases ||
                        !!jurisdiction ||
                        !!systemRef ||
                        !!deviceModel ||
                        !!capacityMin ||
                        capacityMax !== MAX_CAPACITY
                    }
                    unmountOnExit
                    timeout={FILTER_PANEL_SLIDE_MS}
                >
                    <AppButton
                        variant="outlined"
                        onClick={clearAllLocalAndUrl}
                        endIcon={<ClearOutlinedIcon />}
                    >
                        Clear All
                    </AppButton>
                </Fade>
            </FilterTabBarFrame>

            <Grid xs>
                <AppPaper>
                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            <GenSearchResults
                                searchResults={data?.data}
                                orderBy={orderBy}
                                orderDirection={orderDirection}
                                setOrderBy={setOrderBy}
                                isLoading={isLoading}
                                isPopupMode={isPopupMode} // Pass popup mode to search results
                                onDeviceSelect={
                                    isPopupMode ? handleDeviceSelect : undefined
                                } // Only pass handler in popup mode
                            />
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="space-between"
                                    direction={{ xs: "column", sm: "row" }}
                                    width="100%"
                                    spacing={1}
                                >
                                    <div />
                                    {!isFinalPage(page, data.totalResults) && (
                                        <AppLoadingButton
                                            color="primary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: {
                                                    xs: "100%",
                                                    md: "20%",
                                                },
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                    <Typography>
                                        {`Showing ${
                                            isFinalPage(page, data.totalResults)
                                                ? data.totalResults
                                                : page * GEN_PAGE_SIZE
                                        } devices of ${data.totalResults}`}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
            <FilterPanelFrame>
                <DisplayIf condition={!isPopupMode}>
                    <ToggleButtonGroup
                        color="primary"
                        fullWidth
                        value={GENERATION_KW}
                        size="small"
                        exclusive
                        onChange={() =>
                            navigate(
                                DEMAND_DEVICE_SEARCH_NAV_LINK(
                                    LCTOnlyDatasets.HP
                                )
                            )
                        }
                    >
                        <ToggleButton
                            value={DEMAND_KW}
                            sx={{
                                borderRadius: BUTTON_RADIUS_MULTIPLIER,
                                textTransform: "none",
                            }}
                        >
                            {DEMAND_KW}
                        </ToggleButton>
                        <ToggleButton
                            value={GENERATION_KW}
                            disabled
                            sx={{
                                borderRadius: BUTTON_RADIUS_MULTIPLIER,
                                textTransform: "none",
                            }}
                        >
                            {GENERATION_KW}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </DisplayIf>
                <DisplayIf condition={!isManuApproved && !isEnaSysAdmin}>
                    <AppControlledSelectKwarg
                        options={referenceData?.[MANUFACTURER_REF_KEY] || []}
                        value={manufacturer}
                        setValue={(
                            e_: SyntheticEvent<Element, Event>,
                            nv: TFilterSelect
                        ) => setFilterValue(nv, MANUFACTURER_REF_KEY)}
                        label="Manufacturer"
                        loading={refDataLoading}
                        optionName="Manufacturer"
                    />
                </DisplayIf>
                <AppControlledSelectKwarg
                    options={referenceData?.[CATEGORY_REF_KEY] || []}
                    value={category}
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: TFilterSelect
                    ) => setFilterValue(nv, CATEGORY_REF_KEY)}
                    label="Category"
                    loading={refDataLoading}
                    optionName="DeviceCategory"
                />
                <AppControlledSelectKwarg
                    options={referenceData?.[DEVICE_TYPE_REF_KEY] || []}
                    value={deviceType}
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: TFilterSelect
                    ) => setFilterValue(nv, DEVICE_TYPE_REF_KEY)}
                    label="Type"
                    loading={refDataLoading}
                    optionName="DeviceType"
                />
                <Box>
                    <Typography variant="body1" gutterBottom>
                        {`Registered Capacity
                        : ${localSliderValue[0]} - ${localSliderValue[1]}${
                            localSliderValue[1] === MAX_CAPACITY ? "+" : ""
                        } kW`}
                    </Typography>

                    <Slider
                        value={localSliderValue}
                        max={MAX_CAPACITY}
                        onChange={onSlide}
                        onChangeCommitted={setFilterFromSlider}
                        valueLabelDisplay="auto"
                        getAriaLabel={(index) => `capacitySlider${index}`}
                    />
                </Box>
                <AppControlledSelectKwarg
                    options={referenceData?.[COMPLIANCE_STATUS_REF_KEY] || []}
                    value={complianceStatus}
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: TFilterSelect
                    ) => setFilterValue(nv, COMPLIANCE_STATUS_REF_KEY)}
                    label="Compliance Status"
                    loading={refDataLoading}
                    optionName="StatusShort"
                />
                <AppControlledSelectKwarg
                    options={referenceData?.[PHASE_REF_KEY] || []}
                    value={phases}
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: TFilterSelect
                    ) => setFilterValue(nv, PHASE_REF_KEY)}
                    label="Phase"
                    loading={refDataLoading}
                    optionName="Phase"
                />
                <TextField
                    label="System Reference"
                    value={localSystemRef}
                    placeholder="Type to search..."
                    onChange={(event) => {
                        setLocalSystemRef(event.target.value)
                        setFilterFromTextfield(
                            event.target.value,
                            SYSTEM_FILTER_REF_KW
                        )
                    }}
                />
                <TextField
                    label="Model"
                    placeholder="Type to search..."
                    value={localDeviceModel}
                    onChange={(event) => {
                        setLocalDeviceModel(event.target.value)
                        setFilterFromTextfield(
                            event.target.value,
                            DEVICE_MODEL_FILTER_KW
                        )
                    }}
                />
                <AppControlledSelectKwarg
                    options={referenceData?.[JURISDICTION_REF_KEY] || []}
                    value={jurisdiction}
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: TFilterSelect
                    ) => setFilterValue(nv, JURISDICTION_REF_KEY)}
                    label="Jurisdiction"
                    loading={refDataLoading}
                    optionName={JURISDICTION_REF_KEY}
                />
                {(isManuApproved || isGenAssessor || isDemandAssessor) && (
                    <FormControlLabel
                        sx={{ marginLeft: 0 }}
                        control={
                            <Checkbox
                                checked={isThreadUnread}
                                onChange={(e) => {
                                    setFilterFromTextfield(
                                        e.target.checked ? "true" : "false",
                                        "is_thread_unread"
                                    )
                                }}
                            />
                        }
                        label="Read / Unread Comments?"
                    />
                )}

                {(isDnoApproved || isEnaSysAdmin) && (
                    <FormControlLabel
                        sx={{ marginLeft: 0 }}
                        control={
                            <Checkbox
                                checked={isSubscribed}
                                onChange={(e) => {
                                    setFilterFromTextfield(
                                        e.target.checked ? "true" : "false",
                                        "is_subscribed"
                                    )
                                }}
                            />
                        }
                        label="Subscribed / Unsubscribed?"
                    />
                )}
            </FilterPanelFrame>
        </Grid>
    )
}

// Apply defaultProps
GenDeviceSearch.defaultProps = defaultProps

export default GenDeviceSearch
